// src/hooks/use-backend-api.ts
import { useState, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  testBackendConnection,
  callBackendAI,
  type BackendTestResult,
} from "@/services/backend-api-service";
import { type TokenUsage } from "@/lib/types/models";
import { AVAILABLE_MODELS, type ModelDefinition } from "@/lib/types/models";

export type BackendStatus = "idle" | "testing" | "success" | "error";

export interface UseBackendApiReturn {
  // State
  status: BackendStatus;
  message: string;
  selectedModelId: string;
  availableModels: ModelDefinition[];

  // Actions
  setSelectedModelId: (modelId: string) => void;
  testConnection: (customPrompt?: string) => Promise<void>;
  callAI: (
    messages: Array<{ role: "system" | "user" | "assistant"; content: string }>,
    promptId: string,
    activeAccountId?: string,
  ) => Promise<{
    success: boolean;
    content?: string;
    usage?: TokenUsage;
    cost?: number;
    error?: string;
  }>;

  // Utilities
  reset: () => void;
  isLoading: boolean;
}

export function useBackendApi(initialModelId?: string): UseBackendApiReturn {
  const { user } = useUser();
  const availableModels = AVAILABLE_MODELS;

  const [status, setStatus] = useState<BackendStatus>("idle");
  const [message, setMessage] = useState<string>("");
  const [selectedModelId, setSelectedModelId] = useState<string>(() => {
    return (
      initialModelId || availableModels[0]?.id || "gpt-5-nano"
    );
  });

  const getUserId = useCallback(() => {
    return (
      user?.emailAddresses?.[0]?.emailAddress || user?.id || "anonymous-user"
    );
  }, [user]);

  const testConnection = useCallback(
    async (customPrompt?: string) => {
      setStatus("testing");
      setMessage("Testing backend connection...");

      const userId = getUserId();

      try {
        const result: BackendTestResult = await testBackendConnection(
          selectedModelId,
          userId,
          customPrompt,
        );

        if (result.success) {
          setStatus("success");
          setMessage(result.message);
        } else {
          setStatus("error");
          setMessage(result.message);
        }
      } catch (error) {
        setStatus("error");
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        setMessage(`❌ Connection test failed: ${errorMessage}`);
      }
    },
    [selectedModelId, getUserId],
  );

  const callAI = useCallback(
    async (
      messages: Array<{
        role: "system" | "user" | "assistant";
        content: string;
      }>,
      promptId: string,
      activeAccountId?: string,
    ) => {
      const userId = getUserId();

      try {
        return await callBackendAI(
          messages,
          selectedModelId,
          userId,
          promptId,
          activeAccountId,
        );
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error ? error.message : "Unknown error occurred",
        };
      }
    },
    [selectedModelId, getUserId],
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setMessage("");
  }, []);

  const isLoading = status === "testing";

  return {
    status,
    message,
    selectedModelId,
    availableModels,
    setSelectedModelId,
    testConnection,
    callAI,
    reset,
    isLoading,
  };
}
